import { useState, useCallback, useRef, useMemo } from 'react';
import { brandConfig } from '../config/brands';
import { hardNavigate } from '../utils/navigation';
import {
  useExclusiveActors,
  usePublicActors,
  useDeleteExclusiveActor,
  useCreateExclusiveActor,
  useUpdateExclusiveActor,
  useReorderExclusiveActors,
  triggerExclusiveActorFaceScan,
} from '../features/exclusive-actors';
import type { ExclusiveActor } from '../features/exclusive-actors';
import {
  generatePersonAsync,
  generateCharacterSheetAsync,
  suggestNameAsync,
  checkExclusiveActorName,
} from '../features/exclusive-actors/api';
import { ActorGenerationWizard, type WizardData } from '../components/admin/actor-generation/ActorGenerationWizard';
import { toCharacterSheetFiles } from '../features/actors/characterSheet';
import { isCharacterActor, getActorAttributeSummary } from '../features/actors/actorAttributes';
import { ActorFormModal } from '../components/admin/ActorFormModal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import HoverPillButton from '../components/common/HoverPillButton';
import '../styles/admin-pages.css';
import '../styles/buttons.css';
import '../styles/form-controls.css';
import './admin/ActorManagementPage.css';
import './ExclusiveActorsPage.css';

const SUPABASE_URL = 'https://lirsarznxaidebqhgxch.supabase.co';

type GenderFilter = 'all' | 'male' | 'female';

export default function ExclusiveActorsPage() {
  // データ取得
  const exclusiveActorsQuery = useExclusiveActors();
  const publicActorsQuery = usePublicActors();
  const deleteActorMutation = useDeleteExclusiveActor();
  const createActorMutation = useCreateExclusiveActor();
  const updateActorMutation = useUpdateExclusiveActor();
  const reorderMutation = useReorderExclusiveActors();

  // フィルタ状態
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [ageFilter, setAgeFilter] = useState<number | null>(null);
  const [showTags, setShowTags] = useState(false);

  // モーダル状態
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingActor, setEditingActor] = useState<ExclusiveActor | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExclusiveActor | null>(null);

  // ドラッグ&ドロップ用
  const [draggedActorId, setDraggedActorId] = useState<string | null>(null);
  const [dragOverActorId, setDragOverActorId] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // フィルタ適用
  const filterActors = useCallback(
    (actors: ExclusiveActor[]) => {
      return actors.filter((actor) => {
        // 性別フィルタ
        if (genderFilter !== 'all' && actor.gender !== genderFilter) {
          return false;
        }
        // 年齢フィルタ（キャラクターは年齢を持たないため非該当扱いで除外する）
        if (ageFilter !== null) {
          if (isCharacterActor(actor)) {
            return false;
          }
          const minAge = actor.min_age ?? 0;
          const maxAge = actor.max_age ?? 100;
          if (ageFilter < minAge || ageFilter > maxAge) {
            return false;
          }
        }
        return true;
      });
    },
    [genderFilter, ageFilter]
  );

  // フィルタ済みデータ
  const filteredExclusiveActors = useMemo(() => {
    if (!exclusiveActorsQuery.data?.actors) return [];
    return filterActors(exclusiveActorsQuery.data.actors);
  }, [exclusiveActorsQuery.data, filterActors]);

  const filteredPublicActors = useMemo(() => {
    if (!publicActorsQuery.data?.actors) return [];
    return filterActors(publicActorsQuery.data.actors);
  }, [publicActorsQuery.data, filterActors]);

  // フィルタサマリー
  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (genderFilter !== 'all') {
      parts.push(genderFilter === 'male' ? '男性' : '女性');
    }
    if (ageFilter !== null) {
      parts.push(`${ageFilter}歳`);
    }
    return parts.length > 0 ? parts.join(', ') : 'すべて';
  }, [genderFilter, ageFilter]);

  // ウィザード関連
  const handleOpenWizard = () => setIsWizardOpen(true);
  const handleCloseWizard = () => setIsWizardOpen(false);

  const handleWizardComplete = useCallback(
    async (data: WizardData) => {
      if (!data.characterSheetBase64 || !data.faceImageBase64) {
        alert('画像データが不足しています');
        return;
      }

      if (!isCharacterActor({ ethnicity: data.ethnicity }) && data.gender !== 'male' && data.gender !== 'female') {
        alert('性別を選択してください');
        return;
      }

      try {
        const { characterSheetFile, faceImageFile } = await toCharacterSheetFiles({
          characterSheetBase64: data.characterSheetBase64,
          characterSheetMimeType: data.characterSheetMimeType,
          faceImageBase64: data.faceImageBase64,
        });

        await createActorMutation.mutateAsync({
          character_sheet: characterSheetFile,
          face_image: faceImageFile,
          name: data.internalName,
          display_name: data.displayName,
          display_name_en: data.displayNameEn || undefined,
          gender: data.gender !== 'none' ? data.gender : undefined,
          min_age: data.ageMin ?? undefined,
          max_age: data.ageMax ?? undefined,
          ethnicity: data.ethnicity || undefined,
          tags: data.tags,
          body_type: data.bodyType !== 'none' ? data.bodyType : undefined,
          physical_features: data.physicalFeatures !== 'none' ? data.physicalFeatures : undefined,
          // キャラクターの特徴・設定テキスト (Step5・キャラクターのみ)。AIプロンプトへそのまま追加される
          character_traits: data.characterTraits.trim() || undefined,
          generation_prompt: data.prompt || undefined,
        });

        setIsWizardOpen(false);
        exclusiveActorsQuery.refetch();
      } catch (error: any) {
        console.error('Failed to create AI俳優:', error);
        alert(error?.response?.data?.error || error?.message || '作成に失敗しました');
      }
    },
    [createActorMutation, exclusiveActorsQuery]
  );

  // 編集・削除
  const handleEdit = (actor: ExclusiveActor) => {
    setEditingActor(actor);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (actor: ExclusiveActor) => {
    setDeleteTarget(actor);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteActorMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete AI俳優:', error);
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingActor(null);
  };

  const handleModalSuccess = () => {
    exclusiveActorsQuery.refetch();
    handleModalClose();
  };

  const getAgeRange = (minAge: number | null, maxAge: number | null) => {
    if (!minAge && !maxAge) return '-';
    if (minAge && maxAge) return `${minAge}〜${maxAge}歳`;
    if (minAge) return `${minAge}歳〜`;
    if (maxAge) return `〜${maxAge}歳`;
    return '-';
  };
  const getFaceImageUrl = (actor: ExclusiveActor) => {
    if (actor.face_image_bucket && actor.face_image_path) {
      return `${SUPABASE_URL}/storage/v1/object/public/${actor.face_image_bucket}/${actor.face_image_path}`;
    }
    return null;
  };

  // ドラッグ&ドロップ
  const handleDragStart = useCallback((e: React.DragEvent, actorId: string) => {
    setDraggedActorId(actorId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', actorId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedActorId(null);
    setDragOverActorId(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent, actorId: string) => {
      e.preventDefault();
      dragCounter.current++;
      if (actorId !== draggedActorId) {
        setDragOverActorId(actorId);
      }
    },
    [draggedActorId]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverActorId(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetActorId: string) => {
      e.preventDefault();
      setDragOverActorId(null);
      dragCounter.current = 0;

      if (!draggedActorId || draggedActorId === targetActorId || !exclusiveActorsQuery.data) return;

      const actors = [...exclusiveActorsQuery.data.actors];
      const draggedIndex = actors.findIndex((a) => a.id === draggedActorId);
      const targetIndex = actors.findIndex((a) => a.id === targetActorId);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const [draggedActor] = actors.splice(draggedIndex, 1);
      actors.splice(targetIndex, 0, draggedActor);

      const newOrder = actors.map((actor, index) => ({
        id: actor.id,
        display_order: index + 1,
      }));

      try {
        await reorderMutation.mutateAsync(newOrder);
      } catch (error) {
        console.error('Failed to reorder actors:', error);
      }

      setDraggedActorId(null);
    },
    [draggedActorId, exclusiveActorsQuery.data, reorderMutation]
  );

  // 静止画にキャスティングして遷移（AI俳優を選択した状態で静止画生成へ）
  const handleCastToImage = (e: React.MouseEvent, actor: ExclusiveActor) => {
    e.stopPropagation();
    hardNavigate(`/studio/image?model=${actor.id}`);
  };

  // 動画にキャスティングして遷移（AI俳優を選択した状態で動画生成へ）
  const handleCastToVideo = (e: React.MouseEvent, actor: ExclusiveActor) => {
    e.stopPropagation();
    hardNavigate(`/studio/video?model=${actor.id}`);
  };

  // カード描画
  const renderActorCard = (actor: ExclusiveActor, isEditable: boolean) => (
    <div
      key={actor.id}
      className={`actor-card ${isEditable ? '' : 'actor-card-readonly'} ${draggedActorId === actor.id ? 'dragging' : ''} ${dragOverActorId === actor.id ? 'drag-over' : ''}`}
      draggable={isEditable}
      onDragStart={isEditable ? (e) => handleDragStart(e, actor.id) : undefined}
      onDragEnd={isEditable ? handleDragEnd : undefined}
      onDragEnter={isEditable ? (e) => handleDragEnter(e, actor.id) : undefined}
      onDragLeave={isEditable ? handleDragLeave : undefined}
      onDragOver={isEditable ? handleDragOver : undefined}
      onDrop={isEditable ? (e) => handleDrop(e, actor.id) : undefined}
      onClick={isEditable ? () => handleEdit(actor) : undefined}
    >
      {isEditable && <div className="actor-badge actor-badge-exclusive">専属</div>}

      {isEditable && (
        <HoverPillButton
          variant="danger"
          label="削除"
          icon={<img src="/bin_1_white.svg" alt="" />}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(actor);
          }}
          disabled={deleteActorMutation.isPending}
          className="actor-delete-pill"
        />
      )}

      <div className="actor-card-image">
        {getFaceImageUrl(actor) ? (
          <img src={getFaceImageUrl(actor)!} alt={actor.display_name} />
        ) : (
          <div className="actor-card-no-image">No Image</div>
        )}
      </div>
      <div className="actor-card-content">
        <h3 className="actor-card-name">{actor.display_name}</h3>
        <div className="actor-card-meta">
          <span>{getActorAttributeSummary(actor)}</span>
          {!isCharacterActor(actor) && <span>{getAgeRange(actor.min_age, actor.max_age)}</span>}
        </div>
        {showTags && actor.tags && actor.tags.length > 0 && (
          <div className="actor-card-tags">
            {actor.tags.map((tag, idx) => (
              <span key={idx} className="actor-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ホバー時にカード末端から立ち上がるキャスティング選択。
          アイコンは studio の「静止画 / 動画」タブと統一 */}
      <div className="actor-cast-overlay">
        <button
          type="button"
          className="actor-cast-option"
          onClick={(e) => handleCastToImage(e, actor)}
        >
          <img src="/picture_stack_landscape.svg" alt="" className="actor-cast-option-icon" />
          <span>静止画にキャスティング</span>
        </button>
        <button
          type="button"
          className="actor-cast-option"
          onClick={(e) => handleCastToVideo(e, actor)}
        >
          <img src="/video_player_movie.svg" alt="" className="actor-cast-option-icon" />
          <span>動画にキャスティング</span>
        </button>
      </div>
    </div>
  );

  const isLoading = exclusiveActorsQuery.isLoading || publicActorsQuery.isLoading;
  const isError = exclusiveActorsQuery.isError || publicActorsQuery.isError;

  return (
    <div className="page admin-page exclusive-actors-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/composition_window_man.svg" alt="" className="page-title-icon page-title-icon--mobile-hidden" />
            {brandConfig.labels.aiActors}
          </h1>
        </div>
      </div>

      {/* フィルタアコーディオン */}
      <div className={`filter-accordion ${isFilterOpen ? 'filter-accordion-open' : ''}`}>
        <button
          type="button"
          className="filter-accordion-header"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          aria-expanded={isFilterOpen}
        >
          <span className="filter-accordion-title">
            <img src="/filter_sort_lines_descending.svg" alt="" className="filter-accordion-icon" />
            <span className="filter-accordion-label">フィルタ</span>
            <span className="filter-accordion-summary">{filterSummary}</span>
          </span>
          <span className={`filter-accordion-arrow ${isFilterOpen ? 'filter-accordion-arrow-open' : ''}`}>
            {isFilterOpen ? '▼' : '▶'}
          </span>
        </button>
        <div className={`filter-accordion-panel ${isFilterOpen ? 'filter-accordion-panel-open' : ''}`}>
          <div className="filter-grid">
            <div className="filter-group">
              <label className="filter-group-label">性別</label>
              <div className="filter-button-group">
                <button
                  type="button"
                  className={`filter-pill ${genderFilter === 'all' ? 'filter-pill-active' : ''}`}
                  onClick={() => setGenderFilter('all')}
                >
                  指定なし
                </button>
                <button
                  type="button"
                  className={`filter-pill ${genderFilter === 'male' ? 'filter-pill-active' : ''}`}
                  onClick={() => setGenderFilter('male')}
                >
                  男性
                </button>
                <button
                  type="button"
                  className={`filter-pill ${genderFilter === 'female' ? 'filter-pill-active' : ''}`}
                  onClick={() => setGenderFilter('female')}
                >
                  女性
                </button>
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-group-label">年代</label>
              <div className="filter-age-row">
                <button
                  type="button"
                  className={`filter-pill ${ageFilter === null ? 'filter-pill-active' : ''}`}
                  onClick={() => setAgeFilter(null)}
                >
                  指定なし
                </button>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={ageFilter ?? ''}
                  placeholder="30"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setAgeFilter(null);
                    } else {
                      const num = parseInt(val, 10);
                      if (!isNaN(num) && num >= 0 && num <= 100) {
                        setAgeFilter(num);
                      }
                    }
                  }}
                  className={`filter-age-input ${ageFilter !== null ? 'filter-age-input-active' : ''}`}
                />
                <span className="filter-age-unit">歳</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={ageFilter ?? 30}
                  onChange={(e) => setAgeFilter(parseInt(e.target.value, 10))}
                  className={`filter-age-slider ${ageFilter !== null ? 'filter-age-slider-active' : ''}`}
                />
              </div>
            </div>
          </div>

          <div className="filter-tags-row">
            <button
              type="button"
              role="switch"
              className={`filter-tag-switch ${showTags ? 'filter-tag-switch-on' : ''}`}
              onClick={() => setShowTags(!showTags)}
              aria-checked={showTags}
            >
              <span className="filter-tag-switch-label">
                <img src="/tags_1.svg" alt="" className="filter-tag-switch-icon" />
                タグを表示
              </span>
              <span className="filter-tag-switch-track">
                <span className="filter-tag-switch-knob" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {isLoading && <div className="admin-loading">{brandConfig.labels.aiActors}を読み込み中...</div>}

      {isError && <div className="admin-error">{brandConfig.labels.aiActors}の読み込みに失敗しました</div>}

      {!isLoading && !isError && (
        <>
          {/* 専属AI俳優セクション */}
          <section className="actors-section">
            <div className="actors-section-header">
              <h2 className="actors-section-title">専属{brandConfig.labels.aiActor}</h2>
              <span className="actors-section-count">{filteredExclusiveActors.length}人</span>
            </div>
            <p className="page-description actors-section-description">組織内でのみ利用できる独自登録された{brandConfig.labels.aiActor}</p>
            {filteredExclusiveActors.length === 0 &&
            (exclusiveActorsQuery.data?.actors.length ?? 0) > 0 ? (
              <div className="actors-section-empty">
                <p>条件に一致する専属{brandConfig.labels.aiActor}がありません</p>
              </div>
            ) : (
              <div className="actors-grid">
                {filteredExclusiveActors.map((actor) => renderActorCard(actor, true))}
                <button
                  type="button"
                  className="actor-card actor-card-add"
                  onClick={handleOpenWizard}
                >
                  <div className="actor-card-add-content">
                    <span className="actor-card-add-icon">+</span>
                    <span className="actor-card-add-label">専属{brandConfig.labels.aiActor}を生成</span>
                  </div>
                </button>
              </div>
            )}
          </section>

          {/* 公開AI俳優セクション */}
          <section className="actors-section actors-section-readonly">
            <div className="actors-section-header">
              <h2 className="actors-section-title">{brandConfig.labels.aiActors}</h2>
              <span className="actors-section-count">{filteredPublicActors.length}人</span>
            </div>
            <p className="page-description actors-section-description">全ユーザー共通で利用できる{brandConfig.labels.aiActor}</p>
            {filteredPublicActors.length === 0 ? (
              <div className="actors-section-empty">
                <p>条件に一致する{brandConfig.labels.aiActor}がありません</p>
              </div>
            ) : (
              <div className="actors-grid">
                {filteredPublicActors.map((actor) => renderActorCard(actor, false))}
              </div>
            )}
          </section>
        </>
      )}

      {/* 編集モーダル */}
      <ActorFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        actor={editingActor as unknown as import('../components/admin/ActorFormModal').ActorLike | null}
        createFn={(request) => createActorMutation.mutateAsync(request)}
        updateFn={(params) => updateActorMutation.mutateAsync(params)}
        deleteFn={(actorId) => deleteActorMutation.mutateAsync(actorId)}
        onTriggerFaceScan={(actorId) => triggerExclusiveActorFaceScan(actorId)}
        titlePrefix="専属"
      />

      {/* AI俳優生成ウィザード */}
      <ActorGenerationWizard
        isOpen={isWizardOpen}
        onClose={handleCloseWizard}
        onComplete={handleWizardComplete}
        generatePersonApi={generatePersonAsync}
        generateCharacterSheetApi={generateCharacterSheetAsync}
        suggestNameApi={suggestNameAsync}
        checkNameApi={checkExclusiveActorName}
      />

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={`専属${brandConfig.labels.aiActor}を削除`}
        message={`「${deleteTarget?.display_name}」を削除しますか？この操作は取り消せません。`}
        confirmLabel="削除する"
        cancelLabel="キャンセル"
        variant="danger"
        isLoading={deleteActorMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}
